// Font loading and fallback detection. Browser only.

import type { FontSpec } from './types';
import { fontFamilies } from './profiles';

export interface FontLoadResult {
  loaded: string[];
  failed: { id: string; error: string }[];
}

/**
 * Load pinned font files with the FontFace API, then wait for document.fonts.ready.
 * Must finish before any measurement (Pitfall F1). FontFace families shadow local installs (F2).
 */
export async function loadFonts(specs: FontSpec[], urlFor: (spec: FontSpec) => string = s => s.url): Promise<FontLoadResult> {
  const result: FontLoadResult = { loaded: [], failed: [] };
  await Promise.all(
    specs.map(async spec => {
      try {
        const descriptors: Record<string, string> = {};
        for (const [k, v] of Object.entries(spec.descriptors ?? {})) descriptors[k] = String(v);
        const face = new FontFace(spec.family, `url(${JSON.stringify(urlFor(spec))})`, descriptors);
        await face.load();
        document.fonts.add(face);
        result.loaded.push(spec.id);
      } catch (e) {
        result.failed.push({ id: spec.id, error: e instanceof Error ? e.message : String(e) });
      }
    }),
  );
  await document.fonts.ready;
  familyCache.clear();
  return result;
}

const familyCache = new Map<string, boolean>();
const PROBE = 'mmmmmmmmmmlliWWW.,;0123456789 abcdefghijklmnopqrstuvwxyz';

let probeCtx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D | null = null;
function ctx() {
  if (probeCtx) return probeCtx;
  probeCtx = typeof OffscreenCanvas !== 'undefined'
    ? new OffscreenCanvas(1, 1).getContext('2d')!
    : document.createElement('canvas').getContext('2d')!;
  return probeCtx;
}

/** Whether a family really renders: its width must differ from at least one generic fallback. */
export function familyAvailable(family: string): boolean {
  let v = familyCache.get(family);
  if (v !== undefined) return v;
  const c = ctx();
  v = false;
  for (const generic of ['monospace', 'serif', 'sans-serif']) {
    c.font = `40px ${generic}`;
    const base = c.measureText(PROBE).width;
    c.font = `40px ${JSON.stringify(family)}, ${generic}`;
    if (c.measureText(PROBE).width !== base) {
      v = true;
      break;
    }
  }
  familyCache.set(family, v);
  return v;
}

/** Families in a canvas font string that aren't available in this engine. */
export function missingFamilies(font: string): string[] {
  return fontFamilies(font).filter(f => !familyAvailable(f));
}
